import { SwitchChangeEvent } from '@progress/kendo-react-inputs';
import { produce } from 'immer';
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useContext,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  formatPrice,
  showErrorToast,
} from '../../../../../common/global/helpers';
import Icons from '../../../../../common/widgets/Icons';
import Loader from '../../../../../common/widgets/Loader';
import PopoverDialog from '../../../../../common/widgets/PopoverDialog';
import Switch from '../../../../../common/widgets/Switch';
import { config } from '../../../config';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { TagType } from '../../../enums';
import { rewriteImageUrl } from '../../../helpers';
import { ProductSummaryModel } from '../../../models/Cart';
import { resources } from '../../../resources/resources';
import { wishlistService } from '../../../services/wishlist';
import { QuantityChangeType, cartConfig } from '../CartPageCommon';
import { CartPriceLoader } from './CartPriceLoader';
import { mapRecurringFrequency } from './helper';

interface CartProductProps {
  ci: ProductSummaryModel;
  useBenefitBudgetList: number[];
  isCheckoutButtonLoading: boolean;
  handleQuantityChange: (
    cartItemId: number,
    type: QuantityChangeType,
  ) => Promise<void>;
  isSummaryLoading: boolean;
  areQuantitySelectorsDisabled: boolean;
  getIsQuantitySelectorDisabled: (itemId: number) => boolean;
  deleteCartItem: (cartItemId: number) => Promise<void>;
  toggleUseBenefitBudgetCartItem: (
    cartItemId: number,
    currentUseBenefitBudgetList: number[],
  ) => Promise<void>;
  setUseBenefitBudgetList: Dispatch<SetStateAction<number[]>>;
  updateCartItemIsFavorite: (cartItemId: number, isFavorite: boolean) => void;
}

export const CartProduct = (props: CartProductProps) => {
  const {
    ci,
    useBenefitBudgetList,
    isCheckoutButtonLoading,
    handleQuantityChange,
    isSummaryLoading,
    areQuantitySelectorsDisabled,
    getIsQuantitySelectorDisabled,
    deleteCartItem,
    toggleUseBenefitBudgetCartItem,
    setUseBenefitBudgetList,
    updateCartItemIsFavorite,
  } = props;
  const { t: translate } = useTranslation();
  const { customerInfo, setCustomerInfo } = useContext(CustomerInfoContext);
  const addFavoriteAbortControllerRef: MutableRefObject<AbortController> =
    useRef<AbortController>(new AbortController());
  const removeFavoriteAbortControllerRef: MutableRefObject<AbortController> =
    useRef<AbortController>(new AbortController());
  const isFavoriteActionRef = useRef(false);
  const [isFavoriteActionRunning, setIsFavoriteActionRunning] = useState(false);

  const onUseBenefitBudgetChange = (e: SwitchChangeEvent) => {
    const newList = e.value
      ? [...useBenefitBudgetList, ci.cartItemId]
      : useBenefitBudgetList.filter(i => i !== ci.cartItemId);

    setUseBenefitBudgetList(newList);
    toggleUseBenefitBudgetCartItem(ci.cartItemId, newList);
  };

  const addToFavorties = async (e: MouseEvent) => {
    e.preventDefault();
    if (isFavoriteActionRef.current) return;
    isFavoriteActionRef.current = true;

    setIsFavoriteActionRunning(true);
    updateCartItemIsFavorite(ci.cartItemId, true);
    setCustomerInfo(
      produce(customerInfo, d => {
        d.favoriteProductsCount += 1;
      }),
    );

    addFavoriteAbortControllerRef.current = new AbortController();
    removeFavoriteAbortControllerRef.current.abort();

    try {
      const resp = await wishlistService.addProduct(ci.productId, {
        signal: addFavoriteAbortControllerRef.current.signal,
      });
      if (addFavoriteAbortControllerRef.current.signal.aborted) {
        return;
      }
      if (resp.hasErrors) {
        updateCartItemIsFavorite(ci.cartItemId, false);
        setCustomerInfo(
          produce(customerInfo, d => {
            d.favoriteProductsCount -= 1;
          }),
        );
        showErrorToast(translate(resources.errors.generic));
      }
    } finally {
      isFavoriteActionRef.current = false;
      setIsFavoriteActionRunning(false);
    }
  };

  const removeFromFavorties = async (e: MouseEvent) => {
    e.preventDefault();
    if (isFavoriteActionRef.current) return;
    isFavoriteActionRef.current = true;
    setIsFavoriteActionRunning(true);

    updateCartItemIsFavorite(ci.cartItemId, false);
    setCustomerInfo(
      produce(customerInfo, d => {
        d.favoriteProductsCount -= 1;
      }),
    );

    removeFavoriteAbortControllerRef.current = new AbortController();
    addFavoriteAbortControllerRef.current.abort();

    try {
      const resp = await wishlistService.removeProduct(ci.productId, {
        signal: removeFavoriteAbortControllerRef.current.signal,
      });
      if (removeFavoriteAbortControllerRef.current.signal.aborted) {
        return;
      }
      if (resp.hasErrors) {
        updateCartItemIsFavorite(ci.cartItemId, true);
        setCustomerInfo(
          produce(customerInfo, d => {
            d.favoriteProductsCount += 1;
          }),
        );
        showErrorToast(translate(resources.errors.generic));
      }
    } finally {
      isFavoriteActionRef.current = false;
      setIsFavoriteActionRunning(false);
    }
  };
  const isCashbackMethodPaymentEnabled =
    ci.cashbackDiscount > 0 &&
    ci.paidFromContribution === 0 &&
    ci.paidFromBudget === 0;
  const isCashbackMethodPaymentUsed = ci.cashbackDiscount > 0;

  return (
    <li key={ci.cartItemId} className="checkout-list-item">
      <span className="checkout-list-img">
        <span>
          <Link to={`/products/${ci.productId}`}>
            <img
              width="120px"
              height="auto"
              src={rewriteImageUrl(ci.imagePath)}
              alt={ci.name}
            />
          </Link>
        </span>
      </span>
      <div className="checkout-list-details">
        <div className="checkout-list-title">
          <h3 className="checkout-list-name">
            <Link to={`/products/${ci.productId}`}>{ci.name}</Link>
          </h3>
          {ci.isRecurring && (
            <div className="checkout-list-subscription">
              <span className="duration">
                {translate(resources.cart.summary.subscription, {
                  0:
                    (ci.recurringSummary?.duration ?? 0) > 1
                      ? `${ci.recurringSummary?.duration!}`
                      : '',
                  1: translate(
                    mapRecurringFrequency(
                      ci.recurringSummary?.frequency!,
                      ci.recurringSummary?.duration!,
                    ),
                  ),
                })}
              </span>
              <span className="duration-period">{`(${ci.recurringSummary?.monthStart}/${ci.recurringSummary?.yearStart} - ${ci.recurringSummary?.monthEnd}/${ci.recurringSummary?.yearEnd})`}</span>
            </div>
          )}
          {ci.tags
            ?.filter(
              t =>
                t.type === TagType.DisponibilityNextMonthType ||
                t.type === TagType.DisponibilityNowType,
            )
            ?.map(t => (
              <div
                key={t.id}
                className={`checkout-list-availability ${
                  t.type === TagType.DisponibilityNowType
                    ? 'available'
                    : 'available-next-month'
                }`}
              >
                <span className="checkout-list-status">{t.description}</span>
                <span className="checkout-list-status-info">{t.label}</span>
              </div>
            ))}
          {isCashbackMethodPaymentUsed && (
            <div className="checkout-list-availability cashback">
              <span className="checkout-list-status">
                {translate(resources.cart.summary.cashback.tagDescription)}
              </span>
            </div>
          )}
        </div>
        {/* Price and taxes */}
        <div
          className={`checkout-list-value ${
            customerInfo.platformType !== 1
              ? 'checkout-list-value-centered'
              : ''
          } ${isCashbackMethodPaymentUsed ? 'has-cashback' : ''}`}
        >
          {/* Product price */}
          <span
            className={`checkout-list-price ${
              customerInfo.platformType !== 1
                ? 'checkout-list-price-centered'
                : ''
            }`}
          >
            <Loader loading={isSummaryLoading} loader={<CartPriceLoader />}>
              {formatPrice(ci.price + ci.budgetTaxes - ci.cashbackDiscount)}
              {` ${config.currency}`}
              {''}
              {`${
                ci.isRecurring
                  ? `/${translate(resources.cart.summary.month)}`
                  : ''
              }`}
            </Loader>
          </span>
          {isCashbackMethodPaymentUsed && (
            <span
              className={`checkout-list-price cashback ${
                customerInfo.platformType !== 1
                  ? 'checkout-list-price-centered'
                  : ''
              }`}
            >
              <Loader loading={isSummaryLoading} loader={<CartPriceLoader />}>
                {formatPrice(ci.price + ci.budgetTaxes)}
                {` ${config.currency}`}
                {''}
                {`${
                  ci.isRecurring
                    ? `/${translate(resources.cart.summary.month)}`
                    : ''
                }`}
              </Loader>
            </span>
          )}
          {/* Salary taxes */}
          {customerInfo.platformType === 1 && (
            <span className="checkout-list-taxes">
              <Loader loading={isSummaryLoading} loader={<CartPriceLoader />}>
                {formatPrice(ci.salaryTaxes)}
                {` ${config.currency} `}
                {`${translate(resources.cart.summary.salaryTaxes.label)} `}
              </Loader>
            </span>
          )}
        </div>
        {/* Cart quantity and shwitch */}
        <div className="checkout-list-financial">
          {/* Quantity selector */}
          <div className="checkout-list-quantity-group">
            <div className="checkout-list-quantity">
              {!ci.isRecurring && (
                <div className="quantity-selector">
                  <button
                    className="btn btn-product-quantity btn-quantity-remove"
                    onClick={() =>
                      handleQuantityChange(
                        ci.cartItemId,
                        QuantityChangeType.Decrease,
                      )
                    }
                    disabled={
                      ci.quantity <= 1 ||
                      getIsQuantitySelectorDisabled(ci.cartItemId) ||
                      isCheckoutButtonLoading ||
                      areQuantitySelectorsDisabled ||
                      isSummaryLoading
                    }
                  >
                    <span className="btn-icon">
                      <Icons.ProductRemove />
                    </span>
                  </button>
                  <span>{ci.quantity}</span>
                  <button
                    className="btn btn-product-quantity btn-quantity-remove"
                    onClick={() =>
                      handleQuantityChange(
                        ci.cartItemId,
                        QuantityChangeType.Increase,
                      )
                    }
                    disabled={
                      ci.quantity >= cartConfig.maxQuantity ||
                      getIsQuantitySelectorDisabled(ci.cartItemId) ||
                      isCheckoutButtonLoading ||
                      areQuantitySelectorsDisabled ||
                      isSummaryLoading
                    }
                  >
                    <span className="btn-icon">
                      <Icons.ProductAdd />
                    </span>
                  </button>
                </div>
              )}
            </div>
            <div className="checkout-list-actions">
              <button
                onClick={(e: any) =>
                  ci.isFavorite ? removeFromFavorties(e) : addToFavorties(e)
                }
                className={`btn btn-product-action ${
                  ci.isFavorite ? 'active' : ''
                }`}
                disabled={isFavoriteActionRunning}
              >
                <span className="btn-icon">
                  <Icons.Favorite />
                </span>
              </button>
              <button
                onClick={() => deleteCartItem(ci.cartItemId)}
                disabled={isCheckoutButtonLoading || isSummaryLoading}
                className="btn btn-product-action"
              >
                <span className="btn-icon">
                  <Icons.Delete />
                </span>
              </button>
            </div>
          </div>
          {!isCashbackMethodPaymentEnabled && ci.isBenefitInKind ? (
            <div className="checkout-list-budget checkout-list-budget-toggle">
              <Switch
                checked={useBenefitBudgetList.includes(ci.cartItemId)}
                onChange={onUseBenefitBudgetChange}
                disabled={
                  isSummaryLoading ||
                  isCheckoutButtonLoading ||
                  !ci.canUseBenefitBudget
                }
              />
              {translate(resources.cart.summary.useBenefitBudget)}
            </div>
          ) : (
            <span className="checkout-list-budget">
              {ci.budgetTypeMessage}
              {!!ci.budgetTypeInfo && (
                <PopoverDialog
                  className="checkout-info-dropdown"
                  placement="bottom-end"
                  closeFromContent
                  type="hover"
                  content={<span>{ci.budgetTypeInfo}</span>}
                >
                  <span className="btn cart-info-btn">
                    <span className="btn-icon">
                      <Icons.InfoSummary />
                    </span>
                  </span>
                </PopoverDialog>
              )}
            </span>
          )}
        </div>
      </div>
    </li>
  );
};
